<template>
  <VDropdown
    :id="id"
    :ref="`dropdownMenuRef_${id}`"
    :distance="distance"
    :skidding="skidding"
    :placement="placement"
    :triggers="triggers"
    :autoHide="autoHide"
    :class="className"
    :popperClass="popperClass"
    :container="container"
    :shown="shown"
    :delay="delay"
    :disabled="disabled"
    class="dropdown-menu"
    theme="info-dropdown"
    @applyShow="onApplyShow"
    @applyHide="onApplyHide"
    @show="$emit('onShow')"
    @hide="$emit('onHide')"
  >
    <slot
      name="label"
      :hideDropdown="hideDropdown"
    />

    <template #popper>
      <slot
        :hideDropdown="hideDropdown"
        name="before-list"
      />
      <slot
        name="list"
        :hideDropdown="hideDropdown"
      >
        <div
          class="custom-scroll dropdown-menu__list"
          :id="listId"
          :class="{ 'dropdown-menu__empty-list': !values.length }"
        >
          <template v-if="values.length">
            <div
              v-for="(value, index) in values"
              :key="index"
              :ref="`listOfElementRefs_${id}`"
              :class="[
                { 'add-hover': selectedElementIndex < 0 },
                { 'is-selected': index === selectedElementIndex },
                { 'reset-padding': resetPaddingForItem },
                value?.class
              ]"
              class="dropdown-menu__item dropdown-menu--keyboard-navigation__item"
              @click="onSelect({ value, index })"
              @mouseenter="selectedElementIndexLocal = index"
              @mousedown.prevent
            >
              <slot
                name="item"
                :value="value"
                :index="index"
                :hideDropdown="hideDropdown"
              />
            </div>
            <ObserverComponent
              class="dropdown-menu__observer"
              :last="isLast"
              :options="{ threshold: 0.5 }"
              @intersect="$emit('onScroll')"
            />
          </template>
          <template v-else>
            <BaseSubHeading
              class="dropdown-menu__empty"
              size="large"
              weight="400"
              color="monochrome-06"
            >
              {{ noOptionsText }}
            </BaseSubHeading>
          </template>
        </div>
      </slot>

      <slot
        name="after-list"
        :hideDropdown="hideDropdown"
      />
    </template>
  </VDropdown>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"

import ObserverComponent from "@/components/Observer/ObserverComponent.vue"

import eventKey from "@/helpers/eventKeyHelper"

const components = {
  ObserverComponent
}
@Options({
  name: "VDropdownWithKeyboardNavigation",
  components
})
export default class VDropdownWithKeyboardNavigation extends Vue {
  @Prop({ required: true }) id: string
  @Prop({ required: true }) values: any[]
  @Prop({ default: 0 }) readonly skidding: number
  @Prop({ default: 0 }) readonly distance: number
  @Prop({ default: 0 }) readonly delay: number
  @Prop({ default: -1 }) readonly selectedElementIndex: number
  @Prop({ default: "bottom" }) readonly placement: string | undefined
  @Prop({ default: ["click"] }) readonly triggers: string[]
  @Prop({ default: "" }) readonly className: string
  @Prop({ default: "" }) readonly popperClass: string
  @Prop({ default: "body" }) readonly container: string | HTMLElement
  @Prop({ default: null }) readonly noOptionsText: string | null
  @Prop({ default: false }) readonly shown: boolean
  @Prop({ default: true }) readonly isLast: boolean
  @Prop({ default: true }) readonly autoHide: boolean
  @Prop({ default: false }) readonly disabled: boolean
  @Prop({ default: false }) readonly disableHidingBySelect: boolean
  @Prop({ default: false }) readonly resetPaddingForItem: boolean
  @Prop({ default: "" }) readonly listId: string
  isDropdownOpened = false
  keydownDocument: Document = document

  get containerDocument(): Document {
    return typeof this.container === "string" ? document : this.container.ownerDocument
  }

  get selectedElementIndexLocal() {
    return this.selectedElementIndex
  }

  set selectedElementIndexLocal(value) {
    this.$emit("selectedIndexUpdate", value)
  }

  mounted() {
    this.keydownDocument = this.containerDocument
    this.keydownDocument.addEventListener("keydown", this.onKeyDown)
  }

  beforeUnmount() {
    this.keydownDocument.removeEventListener("keydown", this.onKeyDown)
  }

  /** A container from another window (the AI chat PiP) gets its own key presses, which never reach the opener document. */
  @Watch("container")
  onContainerChange() {
    this.keydownDocument.removeEventListener("keydown", this.onKeyDown)
    this.keydownDocument = this.containerDocument
    this.keydownDocument.addEventListener("keydown", this.onKeyDown)
  }

  onKeyDown(event) {
    if (!this.isDropdownOpened) return
    if (eventKey(event).arrowUpKey && this.selectedElementIndex > 0) {
      event.preventDefault()
      this.selectedElementIndexLocal -= 1
      this.$nextTick(() => this.scrollToElement(this.selectedElementIndexLocal))
    }
    if (eventKey(event).arrowDownKey && this.values.length - 1 > this.selectedElementIndex) {
      event.preventDefault()
      this.selectedElementIndexLocal += 1
      this.$nextTick(() => this.scrollToElement(this.selectedElementIndexLocal))
    }
    if (eventKey(event).enterKey && this.selectedElementIndex >= 0) {
      event.preventDefault()
      this.onSelect({
        value: this.values[this.selectedElementIndex],
        index: this.selectedElementIndex
      })
    }
  }

  scrollToElement(index) {
    const listOfElementRefs = this.$refs[`listOfElementRefs_${this.id}`] as any[]
    if (!listOfElementRefs?.[index]) return
    listOfElementRefs[index].scrollIntoView({
      behavior: "instant",
      block: "nearest"
    })
  }

  hideDropdown() {
    const dropdownMenuRef = this.$refs[`dropdownMenuRef_${this.id}`] as any
    if (!dropdownMenuRef) return
    dropdownMenuRef.hide()
  }

  @Emit()
  onSelect({ value, index }) {
    if (!this.disableHidingBySelect) {
      this.hideDropdown()
    }
    return {
      id: this.id,
      value,
      index
    }
  }

  @Emit()
  onApplyShow() {
    this.isDropdownOpened = true
  }

  @Emit()
  onApplyHide() {
    this.isDropdownOpened = false
  }
}
</script>
